import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { ChevronLeft, CheckCircle2, XCircle, BookOpen, ListChecks, AlertCircle, Trophy, Compass } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { Loader, SpeakButton, Bullets, Chips, SectionCard } from "@/components/common";

const LEVELS = [
  { key: "very_simple", label: "Very Simple" },
  { key: "simple", label: "Simple" },
  { key: "standard", label: "Standard" },
  { key: "detailed", label: "Detailed" },
  { key: "advanced", label: "Advanced" },
  { key: "expert", label: "Expert" },
];

function DomainIcon({ name, className }) {
  const map = { wallet: Icons.Wallet, briefcase: Icons.Briefcase, "heart-pulse": Icons.HeartPulse, "shield-check": Icons.ShieldCheck, brain: Icons.Brain, home: Icons.Home, cpu: Icons.Cpu };
  const C = map[name] || Icons.Sparkles;
  return <C className={className} />;
}

function Quiz({ items }) {
  const [answers, setAnswers] = useState({});
  if (!items?.length) return null;
  return (
    <div className="space-y-5" data-testid="lesson-quiz">
      {items.map((q, i) => {
        const chosen = answers[i];
        return (
          <div key={i} className="rounded-md border border-border bg-background p-4">
            <p className="font-medium mb-3">{i + 1}. {q.question}</p>
            <div className="grid gap-2">
              {q.options?.map((opt, oi) => {
                const isAnswer = oi === q.answer_index;
                const picked = chosen === oi;
                let cls = "border-border hover:border-primary";
                if (chosen != null) {
                  if (isAnswer) cls = "border-green-500 bg-green-500/10";
                  else if (picked) cls = "border-destructive bg-destructive/10";
                  else cls = "border-border opacity-60";
                }
                return (
                  <button key={oi} disabled={chosen != null} onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))} data-testid={`quiz-${i}-opt-${oi}`}
                    className={`flex items-center gap-2 text-left rounded-md border-2 px-3 py-2.5 text-sm transition-colors ${cls}`}>
                    {chosen != null && isAnswer && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                    {chosen != null && picked && !isAnswer && <XCircle className="w-4 h-4 text-destructive shrink-0" />}
                    {opt}
                  </button>
                );
              })}
            </div>
            {chosen != null && q.explanation && <p className="text-sm text-muted-foreground mt-3">{q.explanation}</p>}
          </div>
        );
      })}
    </div>
  );
}

export default function Learn() {
  const { mode } = useAppMode();
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState(null);
  const [skill, setSkill] = useState(null);
  const [level, setLevel] = useState("standard");
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState([]);

  useEffect(() => {
    api.get("/content/domains").then((r) => setDomains(r.data.domains)).catch(() => {});
    api.get("/progress").then((r) => setDone(r.data.completed_skills || [])).catch(() => {});
    if (mode === "child") setLevel("simple");
    if (mode === "senior") setLevel("simple");
    if (mode === "professional") setLevel("detailed");
  }, [mode]);

  const openSkill = async (sk, lvl = level) => {
    setSkill(sk); setLesson(null); setLoading(true); setLevel(lvl);
    try {
      const { data } = await api.post("/learn/lesson", { skill: sk.title, domain: domain?.title, mode, level: lvl });
      setLesson(data.error ? null : data);
      if (data.error) toast.error("Lesson unclear, try again.");
    } catch { toast.error("Could not load lesson."); }
    finally { setLoading(false); }
  };

  const markComplete = async () => {
    const { data } = await api.post("/progress/complete-skill", { skill: skill.slug });
    setDone(data.completed_skills);
    toast.success("Skill complete · +50 XP 🎉");
  };

  // Lesson view
  if (skill) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <button onClick={() => { setSkill(null); setLesson(null); }} data-testid="lesson-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" />Back</button>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground font-head">Reading level:</span>
          {LEVELS.map((l) => (
            <button key={l.key} onClick={() => openSkill(skill, l.key)} data-testid={`level-${l.key}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${level === l.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
              {l.label}
            </button>
          ))}
        </div>

        {loading && <Loader label="Building your lesson…" />}

        {lesson && !loading && (
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="font-head font-extrabold text-3xl">{lesson.title}</h1>
                <p className="text-muted-foreground mt-1">{lesson.one_liner}</p>
              </div>
              <SpeakButton text={`${lesson.title}. ${lesson.hook}. ${(lesson.key_points||[]).join(". ")}`} />
            </div>
            {lesson.hook && <p className="text-lg leading-relaxed">{lesson.hook}</p>}
            <SectionCard title="Key points" icon={BookOpen}><Bullets items={lesson.key_points} /></SectionCard>
            {lesson.steps?.length > 0 && <SectionCard title="How to do it" icon={ListChecks}><Bullets items={lesson.steps} /></SectionCard>}
            {lesson.example && <SectionCard title="Real-life example" accent><p className="leading-relaxed">{lesson.example}</p></SectionCard>}
            {lesson.common_mistakes?.length > 0 && <SectionCard title="Common mistakes" icon={AlertCircle}><Bullets items={lesson.common_mistakes} /></SectionCard>}
            {lesson.quiz?.length > 0 && <SectionCard title="Test yourself"><Quiz items={lesson.quiz} /></SectionCard>}
            {lesson.challenge && <SectionCard title="Real-world challenge" icon={Trophy} accent><p className="leading-relaxed">{lesson.challenge}</p></SectionCard>}
            {lesson.related_skills?.length > 0 && <SectionCard title="Connected skills" icon={Compass}><Chips items={lesson.related_skills} /></SectionCard>}

            <button onClick={markComplete} disabled={done.includes(skill.slug)} data-testid="mark-complete"
              className="w-full rounded-full bg-primary text-primary-foreground font-bold py-3.5 hover:brightness-110 disabled:opacity-50 transition">
              {done.includes(skill.slug) ? "✓ Completed" : "Mark skill complete (+50 XP)"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Domain skills view
  if (domain) {
    return (
      <div className="space-y-6">
        <button onClick={() => setDomain(null)} data-testid="domain-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" />All topics</button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl grid place-items-center" style={{ background: `${domain.color}22`, color: domain.color }}><DomainIcon name={domain.icon} className="w-6 h-6" /></div>
          <div><h1 className="font-head font-extrabold text-3xl">{domain.title}</h1><p className="text-muted-foreground">{domain.tagline}</p></div>
        </div>
        {domain.categories.map((cat) => (
          <div key={cat.slug}>
            <h2 className="font-head font-bold text-lg mb-3">{cat.title}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cat.skills.map((sk) => (
                <button key={sk.slug} onClick={() => openSkill(sk)} data-testid={`skill-${sk.slug}`}
                  className="text-left rounded-lg border border-border bg-card p-4 hover:border-primary hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{sk.title}</span>
                    {done.includes(sk.slug) && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                  </div>
                  <span className="text-xs text-muted-foreground">Level {sk.difficulty} · tap to learn</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Domains grid
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl">Learn</h1>
        <p className="text-muted-foreground mt-2">Pick a topic. Every lesson adapts to you and you can change the reading level anytime.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {domains.map((d) => (
          <button key={d.slug} onClick={() => setDomain(d)} data-testid={`domain-${d.slug}`}
            className="text-left rounded-lg border border-border bg-card p-6 hover:border-primary hover:-translate-y-0.5 transition-all">
            <div className="h-12 w-12 rounded-xl grid place-items-center" style={{ background: `${d.color}22`, color: d.color }}><DomainIcon name={d.icon} className="w-6 h-6" /></div>
            <div className="font-head font-bold text-xl mt-3">{d.title}</div>
            <div className="text-sm text-muted-foreground mt-1">{d.tagline}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
